/**
 * The assembly sheet itself, drawn natively as SVG.
 *
 * A faithful port of the drawing half of the backend's matplotlib renderer: the same body
 * palette in the same occlusion order, the same dimension columns marching outward from
 * the widest body, the same diameter block under the tip. The property panel is *not*
 * here — it is real HTML next door, so its text stays selectable and does not zoom.
 *
 * Drawing space is millimetres with z pointing up; SVG y points down, so every z is
 * negated on the way out. Doing it per coordinate rather than with a `scale(1,-1)` group
 * is what keeps the labels from rendering mirrored.
 */

import {
  ARROW_HEAD_PT,
  AXIS_LINE_PT,
  BODY_LINE_PT,
  DIM_FONT_PT,
  DIM_LABEL_OFFSET_PT,
  DIM_LINE_PT,
  DRAWING_COLORS,
  type DrawingLayout,
  GAUGE_LINE_PT,
  LEADER_LINE_PT,
  polygonToPath,
} from '@/components/dialogs/assemblyDrawing/drawingLayout';
import {
  diameterDimensionText,
  noteText,
  verticalDimensionText,
} from '@/components/dialogs/assemblyDrawing/drawingLabels';
import type { AssemblyDrawingSpec, DrawingBody } from '@/types/assemblyDrawing.types';
import type { FC } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

interface AssemblyDrawingSceneProps {
  spec: AssemblyDrawingSpec;
  layout: DrawingLayout;
  /** Current viewBox — the zoom/pan window the viewport hands down. */
  view: { x: number; y: number; width: number; height: number };
  title: string;
}

/** Arrow head geometry inside the marker's own 10x10 box. */
const MARKER_BOX = 10;
const MARKER_STROKE = (DIM_LINE_PT / ARROW_HEAD_PT) * MARKER_BOX;

/** One filled body outline; `null` bodies (no extension, no cutting part) draw nothing. */
const BodyPath: FC<{ body: DrawingBody | null | undefined; fill: string; stroke: string; width: number }> = ({
  body,
  fill,
  stroke,
  width,
}) => {
  if (!body) return null;
  return (
    <path d={polygonToPath(body.polygon)} fill={fill} stroke={stroke} strokeWidth={width} strokeLinejoin="round" />
  );
};

export const AssemblyDrawingScene: FC<AssemblyDrawingSceneProps> = ({ spec, layout, view, title }) => {
  const { t } = useTranslation();
  const markerId = useId();
  const arrowStart = `${markerId}-start`;
  const arrowEnd = `${markerId}-end`;

  const mm = layout.mmPerPoint;
  const fontSize = DIM_FONT_PT * mm;
  const headSize = ARROW_HEAD_PT * mm;
  const bodyWidth = BODY_LINE_PT * mm;
  const dimWidth = DIM_LINE_PT * mm;
  const leaderWidth = LEADER_LINE_PT * mm;
  const labelOffset = DIM_LABEL_OFFSET_PT * mm;
  const { maxR, top, gaugeZ } = layout;

  // matplotlib scales a dash pattern by the line's own width, so the same nominal pattern
  // reads finer on a thin line. Reproduced here, or the leaders come out visibly coarser
  // than on the downloadable PNG.
  const leaderDash = `${1 * LEADER_LINE_PT * mm} ${1.65 * LEADER_LINE_PT * mm}`;
  const gaugeDash = `${6 * GAUGE_LINE_PT * mm} ${4 * GAUGE_LINE_PT * mm}`;
  const axisDash = [8, 3, 1, 3].map((part) => part * AXIS_LINE_PT * mm).join(' ');

  return (
    <svg
      viewBox={`${view.x} ${view.y} ${view.width} ${view.height}`}
      preserveAspectRatio="xMidYMid meet"
      className="h-full w-full select-none"
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <defs>
        {/* Open V heads, matching matplotlib's "<->" arrow style. Sized in user units so
            they keep their point size relative to the sheet at any zoom. */}
        <marker
          id={arrowStart}
          viewBox={`0 0 ${MARKER_BOX} ${MARKER_BOX}`}
          refX={1}
          refY={5}
          markerWidth={headSize}
          markerHeight={headSize}
          markerUnits="userSpaceOnUse"
          orient="auto"
        >
          <path
            d="M 9 1 L 1 5 L 9 9"
            fill="none"
            stroke={DRAWING_COLORS.dim}
            strokeWidth={MARKER_STROKE}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </marker>
        <marker
          id={arrowEnd}
          viewBox={`0 0 ${MARKER_BOX} ${MARKER_BOX}`}
          refX={9}
          refY={5}
          markerWidth={headSize}
          markerHeight={headSize}
          markerUnits="userSpaceOnUse"
          orient="auto"
        >
          <path
            d="M 1 1 L 9 5 L 1 9"
            fill="none"
            stroke={DRAWING_COLORS.dim}
            strokeWidth={MARKER_STROKE}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </marker>
      </defs>

      {/* Bodies, in the order that makes the holder occlude the shank it swallows. */}
      <g>
        <BodyPath
          body={spec.bodies.tool}
          fill={DRAWING_COLORS.tool}
          stroke={DRAWING_COLORS.toolEdge}
          width={bodyWidth}
        />
        <BodyPath
          body={spec.bodies.cutting}
          fill={DRAWING_COLORS.cutting}
          stroke={DRAWING_COLORS.cuttingEdge}
          width={bodyWidth}
        />
        <BodyPath
          body={spec.bodies.extension}
          fill={DRAWING_COLORS.extension}
          stroke={DRAWING_COLORS.extensionEdge}
          width={bodyWidth}
        />
        <BodyPath
          body={spec.bodies.holder}
          fill={DRAWING_COLORS.holder}
          stroke={DRAWING_COLORS.holderEdge}
          width={bodyWidth}
        />
      </g>

      {/* Clamping plane, then the symmetry axis. */}
      <line
        x1={-maxR - 8}
        y1={-gaugeZ}
        x2={maxR + 8}
        y2={-gaugeZ}
        stroke={DRAWING_COLORS.gauge}
        strokeWidth={GAUGE_LINE_PT * mm}
        strokeDasharray={gaugeDash}
      />
      <text
        x={maxR + 9}
        y={-gaugeZ}
        fill={DRAWING_COLORS.gauge}
        fontSize={fontSize}
        dominantBaseline="central"
        textAnchor="start"
      >
        {t('toolManagement.assemblyDrawing.gaugePlane')}
      </text>
      <line
        x1={0}
        y1={5}
        x2={0}
        y2={-(top + 5)}
        stroke={DRAWING_COLORS.axis}
        strokeWidth={AXIS_LINE_PT * mm}
        strokeDasharray={axisDash}
      />

      {/* Vertical dimension columns: arrow, dotted leader per endpoint, rotated label. */}
      {layout.verticalDims.map(({ dim, x }) => {
        const labelZ = Math.max(dim.z_from, dim.z_to) + layout.labelPad;
        return (
          <g key={`${dim.side}-${dim.key}`}>
            <line
              x1={x}
              y1={-dim.z_from}
              x2={x}
              y2={-dim.z_to}
              stroke={DRAWING_COLORS.dim}
              strokeWidth={dimWidth}
              markerStart={`url(#${arrowStart})`}
              markerEnd={`url(#${arrowEnd})`}
            />
            <line
              x1={x}
              y1={-dim.z_from}
              x2={dim.leader_x_from}
              y2={-dim.z_from}
              stroke={DRAWING_COLORS.dimLeader}
              strokeWidth={leaderWidth}
              strokeDasharray={leaderDash}
            />
            <line
              x1={x}
              y1={-dim.z_to}
              x2={dim.leader_x_to}
              y2={-dim.z_to}
              stroke={DRAWING_COLORS.dimLeader}
              strokeWidth={leaderWidth}
              strokeDasharray={leaderDash}
            />
            <text
              x={x}
              y={-labelZ}
              transform={`rotate(-90 ${x} ${-labelZ})`}
              fill={DRAWING_COLORS.dim}
              fontSize={fontSize}
              textAnchor="start"
              dominantBaseline="central"
            >
              {verticalDimensionText(dim, t)}
            </text>
          </g>
        );
      })}

      {/* Diameter block under the tip: one row per diameter, ticks rising to the feature. */}
      {layout.diameterDims.map(({ dim, z }) => {
        const half = dim.value / 2;
        return (
          <g key={dim.key}>
            <line
              x1={-half}
              y1={-z}
              x2={half}
              y2={-z}
              stroke={DRAWING_COLORS.dim}
              strokeWidth={dimWidth}
              markerStart={`url(#${arrowStart})`}
              markerEnd={`url(#${arrowEnd})`}
            />
            <line
              x1={-half}
              y1={-z}
              x2={-half}
              y2={-dim.feature_z}
              stroke={DRAWING_COLORS.dimLeader}
              strokeWidth={leaderWidth}
              strokeDasharray={leaderDash}
            />
            <line
              x1={half}
              y1={-z}
              x2={half}
              y2={-dim.feature_z}
              stroke={DRAWING_COLORS.dimLeader}
              strokeWidth={leaderWidth}
              strokeDasharray={leaderDash}
            />
            <text x={0} y={-z - labelOffset} fill={DRAWING_COLORS.dim} fontSize={fontSize} textAnchor="middle">
              {diameterDimensionText(dim, t)}
            </text>
          </g>
        );
      })}

      {layout.notesZ === null ? null : (
        <text
          x={0}
          y={-layout.notesZ}
          fill={DRAWING_COLORS.dim}
          fontSize={fontSize}
          textAnchor="middle"
          dominantBaseline="hanging"
        >
          {spec.notes.map((note) => noteText(note, t)).join('   ')}
        </text>
      )}
    </svg>
  );
};
